/**
 * Push notification service — initialises Firebase Cloud Messaging for the
 * current user and persists their FCM token in Firestore so the Cloud Function
 * can deliver OS-level push notifications.
 *
 * Call `initPush(uid)` once after the user is fully onboarded.
 *
 * Requirements:
 *   • VITE_VAPID_KEY env var — grab from Firebase Console →
 *     Project Settings → Cloud Messaging → Web Push certificates → Key pair
 */

// Imported dynamically inside initPush, not here: firebase/messaging is ~49 kB
// and is only ever needed once a user is fully onboarded AND has granted
// notification permission. A static import put it in everyone's initial
// download, including signed-out visitors who can never reach this code.
import type { Messaging } from 'firebase/messaging';
import { doc, updateDoc, arrayUnion, getDocFromServer } from 'firebase/firestore';
import { app, db, auth } from './firebase';

// Scope used to register the FCM service worker — must match firebase-messaging-sw.js registration.
// Using a sub-scope avoids conflicts with Vite PWA's sw.js which claims scope /.
const FCM_SW_PATH  = '/firebase-messaging-sw.js';
const FCM_SW_SCOPE = '/firebase-push/';

const VAPID_KEY = import.meta.env.VITE_VAPID_KEY as string | undefined;

let _initialized = false;

export type PushStatus =
  | 'native-unconfigured' // local iOS prototype: APNs/FCM integration still pending
  | 'unsupported'   // browser/platform doesn't support push
  | 'ios-not-pwa'   // iOS Safari: must install as PWA first
  | 'no-vapid'      // VITE_VAPID_KEY not set (config error)
  | 'blocked'       // user explicitly denied permission
  | 'not-requested' // permission not yet asked
  | 'registered'    // token stored in Firestore successfully
  | 'error';        // unexpected error during registration

let _lastStatus: PushStatus = import.meta.env.MODE === 'native' ? 'native-unconfigured' : 'not-requested';

export function getPushStatus(): PushStatus {
  return _lastStatus;
}

/**
 * Request notification permission, register the FCM service worker, obtain a
 * token, and save it to `users/{uid}.fcmTokens` in Firestore.
 *
 * Returns a PushStatus so callers can surface errors to the user.
 * Safe to call multiple times — returns 'registered' immediately after success.
 */
export async function initPush(uid: string): Promise<PushStatus> {
  if (import.meta.env.MODE === 'native') return 'native-unconfigured';
  if (_initialized) return 'registered';

  // Feature detection — service workers + Push API required
  if (
    typeof window === 'undefined' ||
    !('Notification' in window) ||
    !('serviceWorker' in navigator) ||
    !('PushManager' in window)
  ) {
    console.log('[Push] Not supported in this context');
    _lastStatus = 'unsupported';
    return _lastStatus;
  }

  // Don't register on localhost — FCM tokens are origin-scoped, so a localhost
  // token can't deliver to the live site and would pollute the Firestore document.
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    console.log('[Push] Skipping registration on localhost — use the live site to test push delivery');
    return _lastStatus; // leave as 'not-requested'; no UI change needed
  }

  // iOS Safari: Web Push only works from an installed PWA (home-screen icon, iOS 16.4+).
  // Skip silently in regular mobile Safari so we never prompt unnecessarily.
  const isIos = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true;
  if (isIos && !isStandalone) {
    console.log('[Push] iOS: push only supported in installed PWA');
    _lastStatus = 'ios-not-pwa';
    return _lastStatus;
  }

  if (!VAPID_KEY) {
    console.warn(
      '[Push] VITE_VAPID_KEY is not set — push notifications disabled.\n' +
      'Get your key: Firebase Console → Project Settings → Cloud Messaging → Web Push certificates'
    );
    _lastStatus = 'no-vapid';
    return _lastStatus;
  }

  // Ask for permission if not already decided
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') {
    console.log('[Push] Notification permission:', permission);
    _lastStatus = permission === 'denied' ? 'blocked' : 'not-requested';
    return _lastStatus;
  }

  try {
    const swRegistration = await navigator.serviceWorker.register(FCM_SW_PATH, {
      scope: FCM_SW_SCOPE,
    });

    const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
    const messaging: Messaging = getMessaging(app);

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration,
    });

    if (!token) {
      console.log('[Push] getToken returned empty — check VAPID key');
      _lastStatus = 'error';
      return _lastStatus;
    }

    // Always use the Firebase Auth uid for the Firestore write — this is the
    // authoritative uid used for notifications, not currentUser.id from the
    // Firestore document which could differ in edge cases.
    const authUid = auth.currentUser?.uid;
    if (!authUid) {
      console.warn('[Push] No authenticated user — cannot persist token');
      _lastStatus = 'error';
      return _lastStatus;
    }
    if (authUid !== uid) {
      console.warn(`[Push] uid mismatch: passed=${uid} auth=${authUid} — using auth uid`);
    }

    // arrayUnion is idempotent — safe to call on every app launch
    await updateDoc(doc(db, 'users', authUid), {
      fcmTokens: arrayUnion(token),
    });

    // Firestore offline persistence resolves updateDoc optimistically before the
    // server confirms. Read back from the server to verify the token actually
    // persisted (a silent security-rule rejection would otherwise go undetected).
    const serverDoc = await getDocFromServer(doc(db, 'users', authUid));
    const storedTokens = serverDoc.data()?.fcmTokens as string[] | undefined;
    if (!storedTokens?.includes(token)) {
      console.error('[Push] Token not found in server document after write — write was likely rejected');
      _lastStatus = 'error';
      return _lastStatus;
    }

    _initialized = true;
    _lastStatus = 'registered';
    console.log('[Push] Registered successfully, authUid:', authUid);

    // When the app is open (foreground), FCM delivers messages here instead of
    // showing an OS notification. The in-app notification bell already reflects
    // the Firestore write, so we just log it.
    onMessage(messaging, (payload) => {
      console.log('[Push] Foreground message:', payload.notification?.title);
    });

    return _lastStatus;
  } catch (err) {
    console.error('[Push] initPush error:', err);
    _lastStatus = 'error';
    return _lastStatus;
  }
}
